import type { DataTableImportResult } from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';

/** What Lookup lists asks for: the job, the typed name (or none), and the list a re-upload replaces. */
export interface ListImportRequest {
  jobId: string;
  name: string;
  replacesDataTableId?: string;
}

/** The largest list the engine takes (ListImport.MaxBytes); said here before the upload, not after it. */
export const maxListBytes = 20 * 1024 * 1024;

/**
 * The kinds of list the desktop's picker looks for (Excel since CapturePoint d287a096); the engine tells a
 * workbook from text by its bytes and sniffs the delimiter, not the name. The old .xls is not offered.
 */
export const listAccept =
  '.xlsx,.xlsm,.csv,.tsv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,text/csv,text/tab-separated-values,text/plain';

/**
 * Job setup › Lookup lists › Choose file, in the browser. CapturePoint's main process opened a file
 * dialog and handed the engine the chosen path (`import-data-table`); the browser's picker hands
 * over the file itself, so the list travels in the request and the engine host writes it under the
 * job's own import folder before reading it (services/Paige.Engine.Host/ListImport.cs). Closing the
 * picker answers null, which the screen reads as the operator's own decision, as on the desktop.
 */
export function listImport(
  call: EngineCall,
  pick: () => Promise<File | null> = pickListFile,
): (request: ListImportRequest) => Promise<DataTableImportResult | null> {
  return async (request) => {
    const file = await pick();
    if (!file) return null;
    if (file.size > maxListBytes)
      throw new Error(
        'That list is larger than 20 MB. Split it, or keep only the columns a lookup uses.',
      );
    return call<DataTableImportResult>('import-data-table', {
      jobId: request.jobId,
      dataTableName: request.name.trim() || listNameFromFile(file.name),
      dataTableFileName: file.name,
      dataTableContent: await base64Of(file),
      // Present only when re-uploading over an existing list, which keeps its rules working.
      ...(request.replacesDataTableId ? { dataTableId: request.replacesDataTableId } : {}),
    });
  };
}

/**
 * A list added without a typed name is named after its file: "vendor_directory-2026.csv" becomes
 * "vendor directory 2026" (CapturePoint's lookupListNameFromFile, main/lookup-list-name.ts).
 */
export function listNameFromFile(fileName: string): string {
  const base = fileName.replace(/^.*[\\/]/, '').replace(/\.[^.]*$/, '');
  const words = base
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return (words || 'List').slice(0, 160).trim();
}

/** The file's bytes as base64, whatever its encoding: the engine reads the bytes, not the browser's text. */
export async function base64Of(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let start = 0; start < bytes.length; start += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(start, start + 0x8000));
  return btoa(binary);
}

/** The browser's file picker for one list; null when the person closes it. */
function pickListFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = listAccept;
    input.style.display = 'none';
    const done = (file: File | null) => {
      resolve(file);
      input.remove();
    };
    input.onchange = () => done(input.files?.[0] ?? null);
    input.oncancel = () => done(null);
    document.body.appendChild(input);
    input.click();
  });
}
